import { round } from 'es-toolkit';
import { useEffect, useState } from 'preact/hooks';
import toast from 'react-hot-toast';
import useSWR from 'swr';
import Footer from '../../components/footer';
import TopNav from '../../components/top-nav';
import { makeNativePurchase } from '../../global-state/audio-player';
import { showModal } from '../../global-state/session';
import { useSubscriptionStatus } from '../../global-state/usage-data';
import { getUser, useUser } from '../../global-state/user';
import { APIError, fetchDonations, requestApi } from '../../utils/api';
import debugLog from '../../utils/debug';
import { reportSentryError } from '../../utils/error-handling';
import { classNames, wait } from '../../utils/helpers';
import { usePageTitle } from '../../utils/page-title';
import { isAndroidNativeApp, isIOSNativeApp } from '../../utils/platform';
import style from './style.module.scss';

const log = (...args: any[]) => {
  debugLog({ logName: 'pricing', logColor: 'chartreuse' }, ...args);
};

const isInNativeApp = isIOSNativeApp || isAndroidNativeApp;
const prices = {
  subscription: {
    lookupKey: 'alpha_user_monthly_3.99',
    price: '3.99',
    adSkips: 50,
    isAvailable: () => true,
  },
  oneTimePurchase100: {
    lookupKey: '100_forever_ad_skips_for_5.99',
    price: '5.99',
    adSkips: 100,
    isAvailable: () => true,
  },
  oneTimePurchase250: {
    lookupKey: '250_forever_ad_skips_for_13.99',
    price: '13.99',
    adSkips: 250,
    isAvailable: () => !isInNativeApp,
  },
  oneTimePurchase500: {
    lookupKey: '500_forever_ad_skips_for_24.99',
    price: '24.99',
    adSkips: 500,
    isAvailable: () => !isInNativeApp,
  },
} as const;

type PriceLookupKey = (typeof prices)[keyof typeof prices]['lookupKey'];

const availableOneTimePurchases = [
  prices.oneTimePurchase100,
  prices.oneTimePurchase250,
  prices.oneTimePurchase500,
].filter((price) => price.isAvailable());

const isPurchasingText =
  isIOSNativeApp || isAndroidNativeApp ? 'Loading...' : 'Redirecting...';

export default function PricingPage() {
  usePageTitle('Pricing');
  const subscriptionStatus = useSubscriptionStatus();
  const userHasActivePlan = subscriptionStatus === 'active';
  const isSignedIn = Boolean(useUser());
  const [purchasingPriceKey, setPurchasingKey] = useState<PriceLookupKey | ''>('');

  const { data: donations } = useSWR('donations', fetchDonations);

  useAnchor();
  return (
    <>
      <TopNav isTransparent={false} />
      <div class={style.wrapper}>
        <h1 class="mb-6 font-semibold">Pricing</h1>
        {userHasActivePlan ? (
          <p className="mb-6 text-center">Thanks for being on a paid plan!</p>
        ) : (
          <p className="mb-6 text-center">
            Skip ads and support creators with an affordable monthly plan!
          </p>
        )}

        <div class="mx-auto mb-8 grid max-w-4xl gap-8 sm:grid-cols-2">
          <div class="rounded-lg border border-gray-200 bg-white p-8 pt-3 shadow-lg">
            <div class="mb-6 text-center">
              <h2 class="mb-2 text-2xl font-semibold text-gray-900">Alpha User Plan</h2>
              <span class="text-2xl font-semibold">$</span>
              <span class="text-4xl font-extrabold tracking-tight">
                {prices.subscription.price}
              </span>
              <span class="ms-1 text-lg font-normal text-gray-500">/month</span>
            </div>

            <ul class="mb-8 space-y-4">
              <li class="flex items-start">
                <Checkbox />
                <span class="text-gray-700">
                  <strong>
                    50{' '}
                    <a href="#ad-skips" class="underline decoration-gray-300">
                      ad-skips
                    </a>
                  </strong>{' '}
                  per month
                </span>
              </li>
              <li class="flex items-start">
                <Checkbox />
                <span class="text-gray-700">Unused ad-skips roll over</span>
              </li>
              <li class="flex items-start">
                <Checkbox />
                <span class="text-gray-700">Cancel anytime</span>
              </li>
            </ul>
            <SubscriptionBuyButton
              purchasingPriceKey={purchasingPriceKey}
              setPurchasingKey={setPurchasingKey}
              userHasActivePlan={userHasActivePlan}
              isSignedIn={isSignedIn}
            />

            <div>
              <p class="mb-4 text-center text-sm font-medium text-amber-600">
                Early adopter pricing - limited time!
              </p>
              <small class="mt-2 block text-sm font-normal text-gray-500">
                This subscription renews monthly, adding {prices.subscription.adSkips}{' '}
                more ad-skips each month. Using ad-skips{' '}
                <a href="#creator-compensation" class="underline decoration-gray-300">
                  supports podcast creators!
                </a>
              </small>
            </div>
          </div>

          <div class="rounded-lg border border-gray-200 bg-white p-8 pt-3 shadow-lg">
            <div class="mb-6 text-center">
              <h2 class="mb-2 text-2xl font-semibold text-gray-900">
                Buy More Ad-Skips
              </h2>
            </div>

            <div class="mb-6 space-y-4">
              {availableOneTimePurchases.map((config) => (
                <OneTimePurchaseBuyButton
                  priceKey={config.lookupKey}
                  purchasingPriceKey={purchasingPriceKey}
                  setPurchasingKey={setPurchasingKey}
                  userHasActivePlan={userHasActivePlan}
                />
              ))}
            </div>

            {availableOneTimePurchases.length <= 1 && (
              <p class="-mt-2 mb-4 p-4 text-center font-normal italic text-gray-600">
                (check website for more&nbsp;options)
              </p>
            )}

            <p class="mb-2 text-sm font-normal text-gray-500">
              Ad-skips never expire! And as you use them,{' '}
              <a href="#creator-compensation" class="underline decoration-gray-300">
                most of the money is set aside for podcast creators.
              </a>
            </p>
            <p class="mb-2 text-sm font-normal text-gray-500">
              These one-time purchases require an active subscription.
            </p>
            <p></p>
          </div>
        </div>

        <h2 class="text-xl">Common Questions</h2>

        <h3 id="ad-skips" class="text-lg">
          What are "ad-skips" and how are they counted?
        </h3>
        <p>
          We use "ad-skip" credits to keep track of how many ads (and how long of ads)
          are being skipped so we can proportionally compensate creators. A typical ad
          slot is 30 seconds with a few seconds of music on either side, and would count
          for 1 ad-skip. You can see the breakdown below for longer & shorter ad slots.{' '}
          <em>
            We add a bit of wiggle room b/c we often skip a few seconds of silence/music
            around the ad slot.
          </em>
        </p>
        <table class="my-2 border">
          <thead class="bg-gray-50 text-gray-700">
            <tr>
              <th>Skip-Ahead Time</th>
              <th>"Ad-skips" Used</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Under 10 seconds</td>
              <td>0</td>
            </tr>
            <tr>
              <td>10 - 40 seconds</td>
              <td>1</td>
            </tr>
            <tr>
              <td>40 - 90 seconds</td>
              <td>2</td>
            </tr>
            <tr>
              <td>Over 90 seconds</td>
              <td>3</td>
            </tr>
          </tbody>
        </table>
        <p>
          We never charge users more than 10 ad-skips per hour of audio. So a half hour
          podcast would max out at 5-6 ad-skip credits. In practice this affects about
          one in three episodes.
        </p>
        <h3 class="text-lg">Why is a monthly plan required to skip ads?</h3>
        <p>
          For two reasons: first off, the whole point of this project is to give back to
          podcast creators. Second, detecting ads in raw audio is computationally
          intensive and running this service is not cheap.
        </p>
        <p>Of course you can still listen to podcasts without a paid plan.</p>
        <h3 class="text-lg" id="creator-compensation">
          How much is set aside for creators?
        </h3>
        <p class="mb-1">
          Whenever you use an ad-skip from a monthly subscription or one-time purchase,
          we set aside $0.04 (4 cents) to be donated to creators.
        </p>
        <p>
          In the current market, creators typically get paid around 1 - 3 cents for a
          30-second ad slot (minus the ad broker's cut), so our goal is to more than
          compensate them for any lost revenue.
        </p>
        <p>
          Additionally, to preserve user privacy and ensure we don't negatively impact
          advertisers who estimate ad-listens by downloads, we serve all users a cached
          copy of each episode instead of downloading it from the podcast hosting
          service every time.
        </p>
        <div id="paying-creators">
          <h3 class="text-lg">How and when do you pay creators?</h3>
          <p class="mb-1">
            There's no fancy automated system in place currently. I simply Google the
            particular podcast and try to find their donate button or patreon page. If
            they're a for-profit (e.g. NYTimes, iHeartRadio) then I'll sign up for a
            subscription (or two) but never use it. To keep this sustainable for me as a
            solo-founder, I aim to pay all creators owed at least $50 every 6-months
            (and more frequently when I can find the time!)
          </p>
          {donations ? (
            <>
              <h4>Current Stats:</h4>
              <p>
                To date we've donated over{' '}
                <strong>
                  ${(Math.floor(donations.totalPaidCents / 1000) * 10).toLocaleString()}
                </strong>{' '}
                to podcaster creators. Listeners have paid for and used{' '}
                {donations.totalUsedCredits.toLocaleString()} ad-skip credits, which
                gets set aside for podcasters at 4 cents per 30-second ad ($
                {Math.round(donations.totalUsedCredits * 0.04).toLocaleString()} total).
              </p>
              <h4>Breakdown by Podcast:</h4>
              <ul class="list-disc pl-4">
                {donations.podcasts.map((p) => {
                  return (
                    <li key={p.podcast.id} data-podcast-id={p.podcast.id}>
                      {p.podcast.name} - {p.podcast.artist}
                      <br />
                      {p.usedCredits} ad-skip credits, $
                      {(p.usedCredits * 0.04).toFixed(2)} set aside
                      {p.paidCents > 0 &&
                        `, $${(p.paidCents / 100).toFixed(2)} donated`}
                      {(p.donations.length > 0 || p.note) && <br />}
                      {p.note && <em>{p.note}</em>}
                      {p.donations.map((d) => (
                        <a
                          key={d.receiptUrl}
                          href={d.receiptUrl}
                          target="_blank"
                          className="mr-2"
                        >
                          ${d.paidCents / 100} on {d.date} (
                          {d.receiptUrl.split('.').pop()})
                        </a>
                      ))}
                    </li>
                  );
                })}
              </ul>
              <small>
                Podcasts with less than 125 ad-skip credits ($5.00 set aside) used are
                not included in this list for brevity.
              </small>
            </>
          ) : (
            <p class="h-64">Loading donation stats...</p> // Using a large height helps ensure anchor link actually works while loading
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

function CheckboxCircle() {
  return (
    <svg
      class="h-4 w-4 flex-shrink-0 text-blue-600"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
    </svg>
  );
}

function Checkbox() {
  return (
    <svg
      class="mr-3 h-5 w-5 flex-shrink-0 text-green-500"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fill-rule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clip-rule="evenodd"
      ></path>
    </svg>
  );
}

function OneTimePurchaseBuyButton({
  priceKey,
  purchasingPriceKey,
  setPurchasingKey,
  userHasActivePlan,
}: {
  priceKey: PriceLookupKey;
  purchasingPriceKey: PriceLookupKey | '';
  setPurchasingKey: (key: PriceLookupKey | '') => void;
  userHasActivePlan: boolean;
}) {
  const option = Object.values(prices).find((p) => p.lookupKey === priceKey)!;
  return (
    <button
      onClick={() => {
        if (!userHasActivePlan) {
          toast.error(
            'Sorry, you must sign up for a plan before you can buy additional ad-skips'
          );
          return;
        }

        if (purchasingPriceKey) return;
        setPurchasingKey(priceKey);
        makePurchase(priceKey).finally(() => setPurchasingKey(''));
      }}
      class={classNames(
        'flex w-full cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-4 transition-colors hover:border-blue-300',
        purchasingPriceKey === priceKey ? 'opacity-70' : ''
      )}
    >
      <div>
        <span class="font-semibold text-gray-900">
          {purchasingPriceKey === priceKey
            ? isPurchasingText
            : `${option.adSkips} ad-skips`}
        </span>
      </div>
      <div class="text-right">
        <span class="text-xl font-bold text-gray-900">
          <span class="text-[17px] font-normal">$</span>
          {option.price}
        </span>
      </div>
    </button>
  );
}

function SubscriptionBuyButton({
  purchasingPriceKey,
  setPurchasingKey,
  userHasActivePlan,
  isSignedIn,
}: {
  purchasingPriceKey: PriceLookupKey | '';
  setPurchasingKey: (key: PriceLookupKey | '') => void;
  userHasActivePlan: boolean;
  isSignedIn: boolean;
}) {
  const sharedClasses =
    'mb-4 w-full justify-center rounded-lg px-6 py-3 font-semibold text-white text-center transition-colors';
  return userHasActivePlan ? (
    <button
      type="button"
      class={classNames(
        sharedClasses,
        'bg-green-600 focus:outline-none focus:ring-4 focus:ring-blue-200'
      )}
      disabled
    >
      You're on this plan ✓
    </button>
  ) : !isSignedIn ? (
    <button
      type="button"
      class={classNames(
        sharedClasses,
        'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200'
      )}
      onClick={() => showModal('sign-in')}
    >
      Sign in to choose plan
    </button>
  ) : (
    <button
      type="button"
      class={classNames(
        sharedClasses,
        'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200'
      )}
      onClick={() => {
        if (purchasingPriceKey) return;
        setPurchasingKey(prices.subscription.lookupKey);
        makePurchase(prices.subscription.lookupKey).finally(() => setPurchasingKey(''));
      }}
    >
      {purchasingPriceKey === prices.subscription.lookupKey
        ? isPurchasingText
        : 'Choose plan'}
    </button>
  );
}

async function makePurchase(priceLookupKey: PriceLookupKey) {
  if (isIOSNativeApp || isAndroidNativeApp) {
    // @ts-expect-error Not sure how to validate this here since it's dynamic based on platform
    return makeNativePurchase(priceLookupKey);
  }

  const { id: userId, email: userEmail, accessToken } = getUser() || {};

  interface ResponseData {
    data: string;
  }

  let url: ResponseData['data'];
  try {
    const startTime = Date.now();
    log('Requesting Checkout URL');
    const response = (
      await requestApi(
        'https://didgjhjnevdixtjltmho.functions.supabase.co/stripe-user-redirect',
        {
          action: 'checkout',
          userId,
          userEmail,
          priceLookupKey,
          env: location.origin.endsWith('www.adblockpodcast.com') ? 'prod' : 'test',
        },
        { Authorization: `Bearer ${accessToken}` }
      )
    ).json as ResponseData;
    if ('error' in response && typeof response.error === 'string')
      toast.error(response.error);

    if (!response.data) throw new APIError('Invalid response');
    url = response.data;
    const ellapsedMs = Date.now() - startTime;
    log(`Got Checkout URL in ${round(ellapsedMs / 1000, 1)}s. Redirecting...`);
  } catch (error) {
    reportSentryError(error, { context: 'Failed to get Stripe Checkout URL' });
    return;
  }

  location.href = url;
  await wait(2 * 1000); // wait for redirect to happen
}

function useAnchor() {
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        // @ts-expect-error Not sure why Typescript doesn't like offsetTop. Also subtract fixed nav height plus a few pixels
        const scrollPosition = (element.offsetTop as number) - 58;
        window.pageScrollContainer.scrollTo({ top: scrollPosition });
      }
    }
  }, []);
}
